import type {
  GridApi,
  GridReadyEvent,
  PaginationChangedEvent,
  PaginationNumberFormatterParams,
} from 'ag-grid-community';
import { MasterDetailModule } from 'ag-grid-enterprise';
import { ServerSideRowModelModule } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import type { IBlotterRequest } from 'apiConnector/blotters';
import { GridSummary } from 'components/Blotters/Filter/Badges/GridSummary';
import { useHeaderTranslationById, useServerSideGridSummary } from 'components/Blotters/Filter/Badges/hooks';
import { GenericErrorBoundary } from 'components/common-form/builder/form/GenericErrorBoundary';
import { GridWrapperWithTheme } from 'components/shared/Blotter/Grid';
import { serverSideSanitizeFilterModel } from 'components/shared/Blotter/Grid/Hooks/serverSideSanitizeFilterModel';
import { useOnServerSideOnColumnChanged } from 'components/shared/Blotter/Grid/Hooks/useOnServerSideOnColumnChanged';
import { useApplyColumnSettings } from 'components/shared/Blotter/Grid/Hooks/usePersistColumnState';
import { useResetColumns } from 'components/shared/Blotter/Grid/Hooks/useResetColumns';
import { useResetFilter } from 'components/shared/Blotter/Grid/Hooks/useResetFilter';
import { getHeaderForBlotterIntlKey } from 'components/shared/Blotter/utils';
import { useAgGridIntl } from 'hooks/ag-grid/useAgGridIntl';
import type { IBlotterItem } from 'models/IBlotterItem';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import type { ColumnGroupPreferenceState, ColumnPreferenceState, ServerSideBlotterGridProps } from './blotter.model';
import { useDisableColumnOptions } from './Hooks/useDisableColumnFilter';
import { useErrorBoundaryWrapper } from './Hooks/useErrorBoundaryWrapper';
import { useOnCellClickHandler as useOnCellClickedHandler } from './Hooks/useOnCellClickHandler';
import { useOnSelectionChangedHandler } from './Hooks/useOnSelectionChangedHandler';
import { LoadingCell } from './loading-cell';
const PAGINATION = [10, 25, 100, 250];
const rowSelection = { mode: 'singleRow', checkboxes: false, enableClickSelection: true } as const;
export function ServerSideBlotterGrid<Request extends IBlotterRequest, Item = IBlotterItem, Error = undefined>({
  e2e,
  pageSizeState,
  columnState,
  columnGroupState,
  columnDefinitions,
  onBlotterColumnStateChanged = () => undefined,
  onSelectedItemsChanged,
  onSetActiveItem,
  onPageSizeChanged,
  canSetActive,
  onGridReady,
  columnTypes,
  modules = [],
  name,
  logAnalyticName,
  masterDetail,
  serverSideDatasource,
  useNativePagination,
  maxRowsCount,
  setIsTruncated,
  ...gridOptions
}: ServerSideBlotterGridProps<Request, Item, Error>) {
  const getLocaleText = useAgGridIntl();
  const [api, setApi] = useState<GridApi | null>(null);
  const currentColumns: readonly ColumnPreferenceState[] | null = columnState ?? null;
  const currentColumnGroups: readonly ColumnGroupPreferenceState[] | undefined | null = columnGroupState;
  const translationFunc = useMemo(() => getHeaderForBlotterIntlKey(name), [name]);
  const onColumnChanged = useOnServerSideOnColumnChanged(
    currentColumns,
    currentColumnGroups,
    onBlotterColumnStateChanged,
  );
  const onAgGridReady = useCallback(
    (event: GridReadyEvent) => {
      setApi(event.api);
      onGridReady?.(event);
    },
    [onGridReady],
  );
  const onSelectionChangedHandler = useOnSelectionChangedHandler(onSelectedItemsChanged);
  const onCellClickedHandler = useOnCellClickedHandler(onSetActiveItem, canSetActive);
  const colDefs = useDisableColumnOptions(columnDefinitions);
  const agGridModules = useMemo(
    () => [...modules, ...(masterDetail ? [MasterDetailModule] : []), ServerSideRowModelModule],
    [modules, masterDetail],
  );
  const gridErrorContext = { grid: e2e };
  const wrapper = useErrorBoundaryWrapper<Item>(gridErrorContext);
  const resetFilter = useResetFilter(api);
  const resetColumns = useResetColumns(api, logAnalyticName);
  const useBadgeTranslation = useHeaderTranslationById(translationFunc, columnDefinitions);
  const [filters, sortCols] = useServerSideGridSummary(columnState);
  useApplyColumnSettings(api ?? undefined, columnState, columnGroupState, colDefs, serverSideSanitizeFilterModel);

  const onPaginationChanged = useCallback(
    (e: PaginationChangedEvent) => {
      if (e.newPage || e.keepRenderedRows === false) {
        const lastPage =
          maxRowsCount !== undefined ? Math.ceil(maxRowsCount / e.api.paginationGetPageSize()) - 1 : undefined;
        if (lastPage !== undefined && e.api.paginationGetCurrentPage() >= lastPage) {
          setIsTruncated?.(true);
          e.api.paginationGoToPage(lastPage);
        } else {
          setIsTruncated?.(false);
        }
      }
      if (e.newPageSize) {
        const pageSize = e.api.paginationGetPageSize();
        onPageSizeChanged?.(pageSize);
      }
    },
    [onPageSizeChanged, setIsTruncated, maxRowsCount],
  );
  const { formatNumber } = useIntl();
  const paginationFormatter = useCallback(
    (params: PaginationNumberFormatterParams<Item>) => {
      return formatNumber(params.value);
    },
    [formatNumber],
  );

  return (
    <GenericErrorBoundary context={gridErrorContext}>
      <GridSummary
        filters={filters}
        sortCols={sortCols}
        translationFunc={useBadgeTranslation}
        onDismiss={resetFilter}
        onReset={resetColumns}
      />
      <GridWrapperWithTheme e2e={e2e} condensed={false}>
        <AgGridReact<Item>
          serverSideDatasource={serverSideDatasource}
          onSelectionChanged={onSelectionChangedHandler}
          onCellClicked={onCellClickedHandler}
          columnDefs={colDefs}
          animateRows
          cellSelection={true}
          onGridReady={onAgGridReady}
          onColumnPinned={onColumnChanged}
          onColumnVisible={onColumnChanged}
          onColumnMoved={onColumnChanged}
          onSortChanged={onColumnChanged}
          onColumnResized={onColumnChanged}
          onColumnGroupOpened={onColumnChanged}
          onFilterChanged={onColumnChanged}
          onFilterModified={onColumnChanged}
          columnTypes={wrapper(columnTypes)}
          rowSelection={rowSelection}
          sideBar={false}
          sortingOrder={['asc', 'desc', null]}
          getLocaleText={getLocaleText}
          rowModelType="serverSide"
          pagination={useNativePagination}
          loadingCellRenderer={LoadingCell}
          modules={agGridModules}
          rowHeight={48}
          headerHeight={56}
          masterDetail={masterDetail}
          reactiveCustomComponents={false}
          suppressPaginationPanel={!useNativePagination}
          paginationPageSizeSelector={useNativePagination ? PAGINATION : undefined}
          paginationNumberFormatter={useNativePagination ? paginationFormatter : undefined}
          suppressScrollOnNewData={useNativePagination}
          paginationPageSize={useNativePagination ? pageSizeState : undefined}
          onPaginationChanged={useNativePagination ? onPaginationChanged : undefined}
          cacheBlockSize={useNativePagination ? Math.min(250, pageSizeState ?? 50) : pageSizeState}
          maxBlocksInCache={useNativePagination ? 1 : undefined}
          {...gridOptions}
        />
      </GridWrapperWithTheme>
    </GenericErrorBoundary>
  );
}
